import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { preferencesAPI, safetyAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Heart, MessageCircle, Activity, User, Settings, LogOut, Shield, Bell, ChevronRight, Trash2, Mail, Smartphone, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationSettings, setNotificationSettings] = useState({
    push_enabled: false,
    email_enabled: true,
    email_matches: true,
    email_messages: true,
    email_likes: true,
    email_weekly_summary: true
  });

  // Push notification hook
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    isLoading: pushLoading,
    permission: pushPermission,
    subscribe: subscribeToPush, 
    unsubscribe: unsubscribeFromPush,
    error: pushError 
  } = usePushNotifications();

  useEffect(() => {
    loadData();
  }, []);

  // Sync push subscription state with notification settings
  useEffect(() => {
    if (!pushLoading) {
      setNotificationSettings(prev => ({
        ...prev,
        push_enabled: pushSubscribed
      }));
    }
  }, [pushSubscribed, pushLoading]);

  const loadData = async () => {
    try {
      const [prefsRes, blockedRes] = await Promise.all([
        preferencesAPI.get(),
        safetyAPI.getBlocked()
      ]);
      setPreferences(prefsRes.data);
      setBlockedUsers(blockedRes.data);
      
      // Load notification settings
      try {
        const token = localStorage.getItem('token');
        const notifRes = await fetch(`${API_URL}/api/settings/notifications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (notifRes.ok) {
          const notifData = await notifRes.json();
          setNotificationSettings(notifData);
        }
      } catch (e) {
        console.error('Failed to load notification settings:', e);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (newPrefs) => {
    try {
      await preferencesAPI.save(newPrefs);
      setPreferences(newPrefs);
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    }
  };

  const saveNotificationSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newSettings)
      });
      
      if (response.ok) {
        setNotificationSettings(newSettings);
        toast.success('Notification settings saved');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save notification settings');
    }
  };

  const handleNotificationChange = (key, value) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    saveNotificationSettings(newSettings);
  };

  const handleUnblock = async (userId) => {
    try {
      await safetyAPI.unblock(userId);
      setBlockedUsers(blockedUsers.filter(b => b.blocked_user_id !== userId));
      toast.success('User unblocked');
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-lg mx-auto px-6 py-4">
          <h1 className="font-display font-bold text-2xl uppercase text-center tracking-tight text-gray-900">Settings</h1>
        </div>
      </header>

      <main className="pt-20 px-6">
        <div className="max-w-lg mx-auto space-y-8">
          {/* Account */}
          <Card className="bg-white border-gray-100 rounded-3xl shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="font-display text-xl uppercase flex items-center gap-3 tracking-tight text-gray-900">
                <User className="w-5 h-5 text-primary" />
                Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                <div>
                  <p className="font-body font-medium text-gray-900">{user?.name}</p>
                  <p className="font-body text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>
              
              {/* Verification Button */}
              <Button
                data-testid="verify-profile-btn"
                variant="outline"
                className="w-full border-primary text-primary hover:bg-primary hover:text-white"
                onClick={() => navigate('/verify')}
              >
                <Shield className="w-4 h-4 mr-2" />
                Verify Your Profile
              </Button>
            </CardContent>
          </Card>

          {/* Dating Preferences */}
          <Card className="bg-white border-gray-100 rounded-3xl shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="font-display text-xl uppercase flex items-center gap-3 tracking-tight text-gray-900">
                <Heart className="w-5 h-5 text-primary" />
                Dating Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label className="font-body text-gray-600">I'm interested in</Label>
                <Select 
                  value={preferences?.interested_in || 'Everyone'} 
                  onValueChange={(v) => savePreferences({ ...preferences, interested_in: v })}
                >
                  <SelectTrigger data-testid="interested-in-select" className="bg-white border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Men">Men</SelectItem>
                    <SelectItem value="Women">Women</SelectItem>
                    <SelectItem value="Everyone">Everyone</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-body text-gray-600">
                  Age Range: {preferences?.age_range_min || 18} - {preferences?.age_range_max || 50}
                </Label>
                <div className="flex gap-4">
                  <Input
                    type="number"
                    min={18}
                    max={100}
                    className="bg-white border-gray-200"
                    value={preferences?.age_range_min || 18}
                    onChange={(e) => savePreferences({ ...preferences, age_range_min: parseInt(e.target.value) })}
                  />
                  <Input
                    type="number"
                    min={18}
                    max={100}
                    className="bg-white border-gray-200"
                    value={preferences?.age_range_max || 50}
                    onChange={(e) => savePreferences({ ...preferences, age_range_max: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-body">
                  Maximum Distance: {preferences?.max_distance || 50} miles
                </Label>
                <Input
                  type="range"
                  min={1}
                  max={100}
                  className="bg-background"
                  value={preferences?.max_distance || 50}
                  onChange={(e) => savePreferences({ ...preferences, max_distance: parseInt(e.target.value) })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card className="bg-white border-gray-100 rounded-3xl shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="font-display text-xl uppercase flex items-center gap-3 tracking-tight text-gray-900">
                <Bell className="w-5 h-5 text-primary" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Push Notifications */}
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-4 h-4 text-gray-500" />
                    <div>
                      <Label className="font-body text-gray-700">Push Notifications</Label>
                      {!pushSupported && (
                        <p className="font-body text-xs text-gray-400">Not supported in this browser</p>
                      )}
                    </div>
                  </div>
                  <Switch
                    data-testid="push-notifications-switch"
                    checked={pushSubscribed}
                    disabled={!pushSupported || pushLoading}
                    onCheckedChange={async (v) => {
                      if (v) {
                        const success = await subscribeToPush();
                        if (success) {
                          toast.success('Push notifications enabled!');
                        } else if (pushPermission === 'denied') {
                          toast.error('Please enable notifications in your browser settings');
                        }
                      } else {
                        const success = await unsubscribeFromPush();
                        if (success) {
                          toast.success('Push notifications disabled');
                        }
                      }
                    }}
                  />
                </div>
                {pushPermission === 'denied' && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <p className="font-body text-xs text-amber-700">
                      Notifications are blocked. Please enable them in your browser settings.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Email Master Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <Label className="font-body text-gray-700">Email Notifications</Label>
                </div>
                <Switch
                  checked={notificationSettings.email_enabled}
                  onCheckedChange={(v) => handleNotificationChange('email_enabled', v)}
                />
              </div>
              
              {/* Email Subcategories */}
              {notificationSettings.email_enabled && (
                <div className="ml-4 space-y-3 border-l-2 border-gray-100 pl-4">
                  <div className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                    <Label className="font-body text-sm text-gray-600">New Matches</Label>
                    <Switch
                      checked={notificationSettings.email_matches}
                      onCheckedChange={(v) => handleNotificationChange('email_matches', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                    <Label className="font-body text-sm text-gray-600">New Messages</Label>
                    <Switch
                      checked={notificationSettings.email_messages}
                      onCheckedChange={(v) => handleNotificationChange('email_messages', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                    <Label className="font-body text-sm text-gray-600">Someone Likes You</Label>
                    <Switch
                      checked={notificationSettings.email_likes}
                      onCheckedChange={(v) => handleNotificationChange('email_likes', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                    <Label className="font-body text-sm text-gray-600">Weekly Summary</Label>
                    <Switch
                      checked={notificationSettings.email_weekly_summary}
                      onCheckedChange={(v) => handleNotificationChange('email_weekly_summary', v)}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Safety */}
          <Card className="bg-white border-gray-100 rounded-3xl shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="font-display text-xl uppercase flex items-center gap-3 tracking-tight text-gray-900">
                <Shield className="w-5 h-5 text-primary" />
                Safety
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <button 
                className="w-full flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                onClick={() => navigate('/verify')}
              >
                <span className="font-body text-gray-700">Selfie Verification</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
              
              <div>
                <h4 className="font-body text-sm text-gray-500 mb-2">Blocked Users</h4>
                {blockedUsers.length === 0 ? (
                  <p className="font-body text-sm text-gray-400">No blocked users</p>
                ) : (
                  <div className="space-y-2">
                    {blockedUsers.map((block) => (
                      <div 
                        key={block.blocked_user_id}
                        className="flex items-center justify-between p-3 rounded-xl bg-gray-50"
                      >
                        <span className="font-body text-sm text-gray-700">{block.blocked_user_id}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleUnblock(block.blocked_user_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Logout */}
          <Button
            data-testid="logout-btn"
            variant="outline"
            className="w-full border-destructive text-destructive hover:bg-destructive hover:text-white"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
        <div className="max-w-lg mx-auto px-4 py-2 flex items-center justify-around">
          <Button 
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary"
            onClick={() => navigate('/discover')}
          >
            <Activity className="w-5 h-5" />
            <span className="text-xs font-body">Discover</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary"
            onClick={() => navigate('/matches')}
          >
            <Heart className="w-5 h-5" />
            <span className="text-xs font-body">Matches</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary"
            onClick={() => navigate('/messages')}
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs font-body">Messages</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-primary"
            onClick={() => navigate('/profile')}
          >
            <User className="w-5 h-5" />
            <span className="text-xs font-body">Profile</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex flex-col items-center gap-1 text-primary"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs font-body">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};
